import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitMerge, Loader2, ShieldAlert, Split, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { ClientPicker } from "../loans/ClientPicker";
import { api, errorMessage } from "../../lib/api";
import { notifyBandejaChanged } from "../../lib/brand";
import { DissolveSummary } from "./DissolveSummary";

const fileCount = (client) => (client.expediente.files || []).length;
const plural = (n) => `${n} ${n === 1 ? "archivo" : "archivos"}`;

const Confirm = ({ open, title, body, action, onCancel, onConfirm, testId, children, disabled }) => (
  <AlertDialog open={open} onOpenChange={(o) => !o && onCancel()}>
    <AlertDialogContent data-testid={`${testId}-dialog`}>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{body}</AlertDialogDescription>
      </AlertDialogHeader>
      {children}
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testId}-cancel`}>Cancelar</AlertDialogCancel>
        <AlertDialogAction disabled={disabled} onClick={onConfirm} className="bg-[#b42318] text-white hover:bg-[#912018]" data-testid={`${testId}-confirm`}>{action}</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const AdminClientActions = ({ client }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(null);
  const [target, setTarget] = useState(null);
  const [busy, setBusy] = useState(false);
  const [summary, setSummary] = useState(null);
  const files = fileCount(client);
  const noLoans = client.loans.length === 0;
  const bid = encodeURIComponent(client.borrower_id);
  const run = async (fn) => {
    setOpen(null);
    setBusy(true);
    try { await fn(); notifyBandejaChanged(); } catch (err) { toast.error(errorMessage(err, "No se pudo completar la acción, intenta de nuevo")); }
    setBusy(false);
  };
  const dissolve = () => run(async () => setSummary((await api.post(`/client-admin/${bid}/dissolve`, null, { timeout: 300000 })).data));
  const merge = () => run(async () => {
    const r = (await api.post(`/client-admin/${bid}/merge`, { target_borrower_id: target.borrower_id }, { timeout: 300000 })).data;
    toast.success(`Fusionado con ${target.name}: ${plural(r.files)}, ${r.loans} ${r.loans === 1 ? "crédito" : "créditos"}`);
    navigate(`/clientes/${encodeURIComponent(r.target_borrower_id)}`);
  });
  const remove = () => run(async () => {
    const r = (await api.delete(`/client-admin/${bid}`, { timeout: 300000 })).data;
    toast.success(`Cliente eliminado · ${plural(r.por_clasificar)} a Por clasificar`);
    navigate("/");
  });
  if (!client.created_in_intranet && !noLoans) return null;
  return (
    <section className="tr-card space-y-3 border-[#f3d695] p-4" data-testid="admin-client-actions">
      <div className="flex items-center gap-2 text-sm font-bold text-[#182235]">
        <ShieldAlert className="h-4 w-4 text-[#b54708]" /> Acciones de administrador
        {busy && <Loader2 className="h-4 w-4 animate-spin text-[#0f5ef7]" data-testid="admin-actions-busy" />}
      </div>
      <div className="flex flex-wrap gap-2">
        {client.created_in_intranet && noLoans && (
          <button type="button" disabled={busy} onClick={() => setOpen("dissolve")} className="tr-btn-light px-3 py-2 text-xs" data-testid="client-dissolve-button">
            <Split className="h-3.5 w-3.5" /> No es un cliente: repartir archivos
          </button>
        )}
        {client.created_in_intranet && (
          <button type="button" disabled={busy} onClick={() => { setTarget(null); setOpen("merge"); }} className="tr-btn-light px-3 py-2 text-xs" data-testid="client-merge-button">
            <GitMerge className="h-3.5 w-3.5" /> Fusionar con otro cliente
          </button>
        )}
        {noLoans && (
          <button type="button" disabled={busy} onClick={() => setOpen("delete")} className="tr-btn-danger px-3 py-2 text-xs" data-testid="client-delete-button">
            <Trash2 className="h-3.5 w-3.5" /> Eliminar cliente
          </button>
        )}
      </div>
      <Confirm open={open === "dissolve"} testId="client-dissolve" title="¿Repartir archivos y eliminar este cliente?" action="Repartir y eliminar"
        body={`Sus ${plural(files)} se asignarán al cliente cuyo nombre aparezca en el nombre del archivo o de sus carpetas; los demás irán a Por clasificar. Después se eliminará "${client.name}".`}
        onCancel={() => setOpen(null)} onConfirm={dissolve} />
      <Confirm open={open === "merge"} testId="client-merge" title="Fusionar con otro cliente" action="Fusionar" disabled={!target}
        body={`Se moverán ${plural(files)} y ${client.loans.length} ${client.loans.length === 1 ? "crédito" : "créditos"} al cliente elegido, y se eliminará "${client.name}".`}
        onCancel={() => setOpen(null)} onConfirm={merge}>
        <ClientPicker value={target} onChange={(c) => setTarget(c && c.borrower_id === client.borrower_id ? null : c)} testPrefix="merge-target" />
      </Confirm>
      <Confirm open={open === "delete"} testId="client-delete" title="¿Eliminar cliente?" action="Eliminar cliente"
        body={`"${client.name}" tiene ${plural(files)}. Irán a Por clasificar (motivo "Cliente eliminado"); ningún archivo se pierde.`}
        onCancel={() => setOpen(null)} onConfirm={remove} />
      <DissolveSummary result={summary} onClose={() => { setSummary(null); navigate("/"); }} />
    </section>
  );
};
